'use client';

import { Suspense } from 'react';

import type { DailyDeal } from '@eliadmin/core/src/api/daily-deals/daily-deals-api';

import { ProductBottomInfo } from '@eliadmin/core/src/components/product/detail/product-bottom-info';
import { ProductDetailAvailability } from '@eliadmin/core/src/components/product/detail/product-detail-availability';
import { ProductDetailPrice } from '@eliadmin/core/src/components/product/detail/product-detail-price';
import { ProductHeader } from '@eliadmin/core/src/components/product/detail/product-header';
import { ProductImages } from '@eliadmin/core/src/components/product/detail/product-images';
import { ProductPaymentMethods } from '@eliadmin/core/src/components/product/detail/product-payment-methods';
import { ProductQuantity } from '@eliadmin/core/src/components/product/detail/product-quantity';
import { ProductVariants } from '@eliadmin/core/src/components/product/detail/variants/product-variants';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';

import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { VariantsParamsSync, VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Button } from '@eliadmin/core/src/components/ui/button';
import Link from '@eliadmin/core/src/components/link';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { fetchDailyDeals } from '@eliadmin/core/src/api/daily-deals/daily-deals-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

export function Deal({ deal, locale }: { deal: DailyDeal; locale: string }) {
  const { me, isInitialized } = useCustomerStore();
  const { data } = useQuery({
    queryKey: [
      'HOMEPAGE_DAILY_DEAL',
      locale,
      deal.id,
      me?.customer.id ?? null,
      me?.authToken ?? null,
    ],
    queryFn: () =>
      fetchDailyDeals({
        locale,
        authToken: me?.authToken,
        query: { page: 1, results: 2 },
        where: { is_running: { equals: true } },
      }),
    enabled: isInitialized && Boolean(me?.authToken),
  });
  const displayedDeal = data?.data[0] ?? deal;
  const dealItem = displayedDeal.items[0];
  const product = dealItem?.product;
  const selectedVariant = dealItem?.variant ?? undefined;

  if (!product) return null;

  return (
    <VariantsProvider
      key={`${displayedDeal.id}-${me?.customer.id ?? 'public'}-${data ? 'personalized' : 'public'}`}
      state={{ product, selectedVariant, selectedQuantity: product.min_quantity ?? 1 }}
    >
      <Suspense>
        <VariantsParamsSync />
      </Suspense>
      <DialogProvider>
        <section
          className={cn(
            'custom-section',
            'pb-6 pt-6 sm:pb-8 sm:pt-12 md:pb-10 md:pt-16 lg:pb-12 lg:pt-24',
          )}
        >
          <div
            className={cn(
              'w-full max-w-[1152px]',
              'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between lg:gap-12',
            )}
          >
            <CarouselProvider>
              <ProductImages inDeal />
            </CarouselProvider>
            <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[545px]">
              <ProductHeader deal={displayedDeal.name} />
              <ProductVariants />
              <ProductDetailAvailability />
              <ProductDetailPrice />
              <ProductQuantity />
              <ProductPaymentMethods />
              <ProductBottomInfo inDeal />
              <Separator />
              <Button asChild variant={'white'} className="w-full sm:w-fit">
                <Link href={product?.slug} prefetchOnHover>
                  Celý detail produktu <RiArrowRightUpLine size={20} />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </DialogProvider>
    </VariantsProvider>
  );
}
